/**
 * @file 地图相关工具函数
 * @author jamie.zhan 2022-09-08
 */
import L from 'leaflet'

export interface MapRegion {
  bounds: L.LatLngBounds;
  zoomLevel: number;
}

// 后端常见的Bbox类型
export interface Bbox {
  downLeft?: {
    lat?: number | null;
    lng?: number | null;
  } | null;
  upperRight?: {
    lat?: number | null;
    lng?: number | null;
  } | null;
}

// 航线用的Bbox类型，ES引擎返回的原始数据类型
export interface EsBbox {
  topLeft?: {
    lat?: number | null;
    lng?: number | null;
  } | null;
  bottomRight?: {
    lat?: number | null;
    lng?: number | null;
  } | null;
}

// 后端bbox数据专场Leaflet的bounds类型
export function bbox2Bounds(bbox: Bbox) {
  const { downLeft, upperRight } = bbox || {}

  if (typeof downLeft?.lat === 'number'
    && typeof downLeft?.lng === 'number'
    && typeof upperRight?.lat === 'number'
    && typeof upperRight?.lng === 'number'
  ) {
    return new L.LatLngBounds(
      { lat: downLeft.lat, lng: downLeft.lng },
      { lat: upperRight.lat, lng: upperRight.lng }
    )
  }
  return undefined
}

// 后端es bbox数据转成Leaflet的bounds类型，用于飞行航线的bbox
export function esBbox2Bounds(bbox: EsBbox) {
  const { bottomRight, topLeft } = bbox || {}

  if (typeof bottomRight?.lat === 'number'
    && typeof bottomRight?.lng === 'number'
    && typeof topLeft?.lat === 'number'
    && typeof topLeft?.lng === 'number'
  ) {
    return new L.LatLngBounds(
      { lat: bottomRight.lat, lng: topLeft.lng },
      { lat: topLeft.lat, lng: bottomRight.lng }
    )
  }
  return undefined
}

// Leaflet的bounds类型转成后端bbox数据
export function bounds2Bbox(bounds: L.LatLngBounds) {
  return {
    downLeft: bounds.getSouthWest().wrap(),
    upperRight: bounds.getNorthEast().wrap()
  }
}

// Leaflet的bounds类型转成后端es bbox数据，用于飞行航线的bbox
export function bounds2EsBbox(bounds: L.LatLngBounds) {
  return {
    topLeft: bounds.getNorthWest().wrap(),
    bottomRight: bounds.getSouthEast().wrap()
  }
}

// 为了地块圆形障碍物的json数据与遥控器一样，这里计算距离的代码直接搬运了android系统中的代码
// https://android.googlesource.com/platform/frameworks/base/+/refs/heads/master/location/java/android/location/Location.java
export function computeDistance(c1: L.LatLng, c2: L.LatLng) {
  let lat1 = c1.lat
  let lat2 = c2.lat
  let lon1 = c1.lng
  let lon2 = c2.lng
  // Based on http://www.ngs.noaa.gov/PUBS_LIB/inverse.pdf
  // using the "Inverse Formula" (section 4)
  // Convert lat/long to radians
  lat1 *= Math.PI / 180.0
  lat2 *= Math.PI / 180.0
  lon1 *= Math.PI / 180.0
  lon2 *= Math.PI / 180.0
  const a = 6378137.0 // WGS84 major axis
  const b = 6356752.3142 // WGS84 semi-major axis
  const f = (a - b) / a
  const aSqMinusBSqOverBSq = (a * a - b * b) / (b * b)
  const l = lon2 - lon1
  let aA = 0.0
  const u1 = Math.atan((1.0 - f) * Math.tan(lat1))
  const u2 = Math.atan((1.0 - f) * Math.tan(lat2))
  const cosU1 = Math.cos(u1)
  const cosU2 = Math.cos(u2)
  const sinU1 = Math.sin(u1)
  const sinU2 = Math.sin(u2)
  const cosU1cosU2 = cosU1 * cosU2
  const sinU1sinU2 = sinU1 * sinU2
  let sigma = 0.0
  let deltaSigma = 0.0
  let cosSqAlpha
  let cos2SM
  let cosSigma
  let sinSigma
  let cosLambda = 0.0
  let sinLambda = 0.0
  let lambda = l // initial guess
  for (let iter = 0; iter < 20; iter++) {
    const lambdaOrig = lambda
    cosLambda = Math.cos(lambda)
    sinLambda = Math.sin(lambda)
    const t1 = cosU2 * sinLambda
    const t2 = cosU1 * sinU2 - sinU1 * cosU2 * cosLambda
    const sinSqSigma = t1 * t1 + t2 * t2
    sinSigma = Math.sqrt(sinSqSigma)
    cosSigma = sinU1sinU2 + cosU1cosU2 * cosLambda
    sigma = Math.atan2(sinSigma, cosSigma)
    const sinAlpha = sinSigma === 0 ? 0.0 : (cosU1cosU2 * sinLambda) / sinSigma
    cosSqAlpha = 1.0 - sinAlpha * sinAlpha
    cos2SM = cosSqAlpha === 0 ? 0.0 : cosSigma - (2.0 * sinU1sinU2) / cosSqAlpha
    const uSquared = cosSqAlpha * aSqMinusBSqOverBSq
    aA = 1 + (uSquared / 16384.0) * (4096.0 + uSquared * (-768 + uSquared * (320.0 - 175.0 * uSquared)))
    const bB = (uSquared / 1024.0) * (256.0 + uSquared * (-128.0 + uSquared * (74.0 - 47.0 * uSquared)))
    const cC = (f / 16.0) * cosSqAlpha * (4.0 + f * (4.0 - 3.0 * cosSqAlpha))
    const cos2SMSq = cos2SM * cos2SM
    deltaSigma =
      bB *
      sinSigma *
      (cos2SM +
        (bB / 4.0) *
          (cosSigma * (-1.0 + 2.0 * cos2SMSq) -
            (bB / 6.0) * cos2SM * (-3.0 + 4.0 * sinSigma * sinSigma) * (-3.0 + 4.0 * cos2SMSq)))
    lambda =
      l +
      (1.0 - cC) * f * sinAlpha * (sigma + cC * sinSigma * (cos2SM + cC * cosSigma * (-1.0 + 2.0 * cos2SM * cos2SM)))
    const delta = (lambda - lambdaOrig) / lambda
    if (Math.abs(delta) < 1.0e-12) {
      break
    }
  }
  const result = b * aA * (sigma - deltaSigma)
  return new Float32Array([result])[0]
}
export function getMapToken(isFirstParam = false) {
  const token = localStorage.getItem('x-auth-token') || ''
  if (IS_PERSONAL_VERSION && token) {
    return isFirstParam ? `?x_auth_token=${token}` : `&x_auth_token=${token}`
  }
  return ''
}
